import * as React from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  Radio,
  ParagraphText,
  StackLayout,
  Icon,
  Fieldset,
} from '@leagueplatform/genesis-core';

/**
 * Props for the MoveToSection component
 * @interface MoveToSectionProps
 * @property {string[]} moveTargets - Array of possible target locations to move items to
 * @property {string} selectedTarget - Currently selected target location
 * @property {(target: string) => void} onTargetSelect - Callback function when a target is selected
 */
export interface MoveToSectionProps {
  moveTargets?: { breadcrumb: string; targetPath: string }[];
  selectedTarget: string | null;
  onTargetSelect: (target: string) => void;
}

/**
 * A section component that displays the "Move To" section of the modal.
 * Contains a form field with radio options for selecting the target location.
 * Handles empty state when no targets are available.
 *
 * @component
 * @example
 * ```tsx
 * <MoveToSection
 *   moveTargets={["Location 1", "Location 2"]}
 *   selectedTarget="Location 1"
 *   onTargetSelect={(target) => console.log(`Selected ${target}`)}
 * />
 * ```
 */
export const MoveToSection: React.FC<MoveToSectionProps> = ({
  moveTargets = [],
  selectedTarget,
  onTargetSelect,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Box css={{ minHeight: 400 }}>
      <Fieldset
        id="move-to-fieldset"
        legend={
          <ParagraphText css={{ fontWeight: '$bold', fontSize: '$bodyTwo' }}>
            {formatMessage({
              id: 'MOVE_TO_LABEL',
            })}
          </ParagraphText>
        }
      >
        {moveTargets?.length === 0 ? (
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: '$half',
              marginBlockStart: '$threeQuarters',
            }}
          >
            <Icon icon="tinyAlert" tint="$warningIcon" />
            <ParagraphText>
              {formatMessage({
                id: 'NO_APPLICABLE_LOCATIONS',
              })}
            </ParagraphText>
          </Box>
        ) : (
          <StackLayout
            orientation="vertical"
            spacing="$half"
            css={{ maxHeight: 400, overflowY: 'auto' }}
          >
            {moveTargets?.map((target, idx) => {
              const radioId = `move-target-radio-${idx}`;
              const lastIndex = moveTargets.length - 1;
              const label = (
                <ParagraphText css={{ fontSize: '$bodyTwo' }}>
                  {target.breadcrumb}
                </ParagraphText>
              );
              return (
                <Box
                  key={target.targetPath}
                  css={{
                    width: '100%',
                    borderBottom:
                      idx === lastIndex
                        ? 'none'
                        : 'solid $colors$onSurfaceBorderSubdued $borderWidths$thin',
                  }}
                >
                  <Radio
                    id={radioId}
                    name="move-target"
                    value={target.targetPath}
                    checked={selectedTarget === target.targetPath}
                    onChange={() => onTargetSelect(target.targetPath)}
                    label={label}
                    css={{ '.GDS-radio-label': { width: '100%' } }}
                  />
                </Box>
              );
            })}
          </StackLayout>
        )}
      </Fieldset>
    </Box>
  );
};
